
import React, { useEffect, useCallback, useState } from 'react';
import { CampingSite } from '@/services/campingSite/types';
import { useMapSiteBookingStatus } from '../hooks/useMapSiteBookingStatus';

// Module-scope static data — stable identity prevents effect re-trigger loops
const LOCATION_COORDINATES: Record<string, google.maps.LatLngLiteral & { zoom?: number }> = {
  'mountain-view': { lat: 41.9397, lng: -116.1633 },
  'sheep-creek': { lat: 41.9580, lng: -116.0967 },
  'wild-horse': { lat: 41.6707, lng: -115.8010, zoom: 13 },
  'north': { lat: 41.6780, lng: -115.7320, zoom: 15 },
  'south': { lat: 41.6720, lng: -115.7380, zoom: 15 },
  'east': { lat: 41.6700, lng: -115.7290, zoom: 15 },
};

interface MapInitializerProps {
  mapRef: React.RefObject<HTMLDivElement>;
  googleMapRef: React.MutableRefObject<google.maps.Map | null>;
  sites: CampingSite[];
  locationFilter: string;
  mapInitialized: boolean;
  setMapInitialized: (initialized: boolean) => void;
  setMapError: (error: string | null) => void;
  markers: React.MutableRefObject<(google.maps.Marker | google.maps.marker.AdvancedMarkerElement)[]>;
  onSiteSelect: (siteId: string) => void;
  mapsLoaded: boolean;
  siteType: 'rv' | 'wild-horse';
  startDate?: Date;
  endDate?: Date;
}

const MapInitializer: React.FC<MapInitializerProps> = ({
  mapRef,
  googleMapRef,
  sites,
  locationFilter,
  mapInitialized,
  setMapInitialized,
  setMapError,
  markers,
  onSiteSelect,
  mapsLoaded,
  siteType,
  startDate,
  endDate
}) => {
  const [markerVersion, setMarkerVersion] = useState(0);

  // Clear existing markers before adding new ones
  const clearMarkers = useCallback(() => {
    markers.current.forEach(marker => {
      if (marker instanceof google.maps.Marker) {
        marker.setMap(null);
      } else {
        // For AdvancedMarkerElement, use map property instead of setMap method
        marker.map = null;
      }
    });
    markers.current = [];
  }, [markers]);

  // Get the proper default center and zoom based on site type
  const getDefaultCenter = useCallback(() => {
    if (siteType === 'wild-horse') {
      const wildHorseLocation = LOCATION_COORDINATES['wild-horse'];
      return {
        center: { lat: wildHorseLocation.lat, lng: wildHorseLocation.lng },
        zoom: wildHorseLocation.zoom || 13
      };
    }
    return {
      center: LOCATION_COORDINATES['mountain-view'],
      zoom: 13
    };
  }, [siteType]);

  // Create markers for all sites
  const createMarkersForSites = useCallback((map: google.maps.Map, sitesToShow: CampingSite[]) => {
    console.log(`[MapInitializer] Creating markers for ${sitesToShow.length} sites`);
    console.log(`[MapInitializer] Sites being processed:`, sitesToShow.map(s => ({ 
      id: s.id, 
      siteNumber: s.siteNumber,
      latitude: s.latitude,
      longitude: s.longitude 
    })));
    
    sitesToShow.forEach((site, index) => {
      if (!site.latitude || !site.longitude) {
        console.warn(`[MapInitializer] Site ${site.id} (${site.siteNumber}) missing coordinates`);
        return;
      }

      const position = { lat: site.latitude, lng: site.longitude };
      const iconUrl = '/assets/icons/tepee (1).png';

      console.log(`[MapInitializer] Creating marker for site ID: ${site.id}, siteNumber: ${site.siteNumber} at position:`, position);

      // Create marker container
      const container = document.createElement('div');
      container.className = 'marker-container';
      container.style.cursor = 'pointer';
      container.setAttribute('data-site-id', site.id);
      container.setAttribute('data-site-number', site.siteNumber || 'unknown');

      const iconElement = document.createElement('img');
      iconElement.src = iconUrl;
      iconElement.alt = `${site.siteNumber || 'Campsite'} Marker`;
      iconElement.width = 32;
      iconElement.height = 32;

      const labelElement = document.createElement('div');
      labelElement.textContent = site.siteNumber || `${site.id}`;
      labelElement.style.backgroundColor = '#22c55e'; // default green - will be updated by SiteMarkerUpdater
      labelElement.style.color = 'white';
      labelElement.style.borderRadius = '4px';
      labelElement.style.padding = '1px 6px';
      labelElement.style.fontSize = '10px';
      labelElement.style.fontWeight = 'bold';
      labelElement.style.textAlign = 'center';
      labelElement.style.marginTop = '-2px';
      labelElement.style.border = '2px solid #f0fdf4';

      container.appendChild(iconElement);
      container.appendChild(labelElement);

      // Create marker
      let marker;
      try {
        marker = new google.maps.marker.AdvancedMarkerElement({
          position,
          title: `${site.siteNumber || `Site ${site.id}`}`,
          map,
          content: container
        });
        
        // Store site ID directly on the marker for easy reference
        (marker as any).siteId = site.id;
        (marker as any).siteNumber = site.siteNumber;
        
      } catch (err) {
        console.warn('AdvancedMarkerElement not available, falling back to regular marker');
        // Fallback to normal marker
        marker = new google.maps.Marker({
          position,
          map,
          title: `${site.siteNumber || `Site ${site.id}`}`,
          icon: {
            url: iconUrl,
            scaledSize: new google.maps.Size(32, 32),
          }
        });
        
        // Store site ID directly on the marker for easy reference
        (marker as any).siteId = site.id;
        (marker as any).siteNumber = site.siteNumber;
      }

      // Add click handler
      const clickHandler = () => {
        if (googleMapRef.current) {
          const currCenter = googleMapRef.current.getCenter();
          const currZoom = googleMapRef.current.getZoom();
          if (currCenter && currZoom) {
            sessionStorage.setItem('mapCenter', JSON.stringify({
              lat: currCenter.lat(),
              lng: currCenter.lng(),
            }));
            sessionStorage.setItem('mapZoom', String(currZoom));
          }
        }
        console.log(`[MapInitializer] Site ${site.id} (${site.siteNumber}) clicked`);
        onSiteSelect(site.id);
      };

      if (marker instanceof google.maps.marker.AdvancedMarkerElement && container) {
        container.addEventListener('click', clickHandler);
      } else if (marker instanceof google.maps.Marker) {
        marker.addListener('click', clickHandler);
      }

      if (marker) {
        markers.current.push(marker);
        console.log(`[MapInitializer] Added marker for site ${site.id} (${site.siteNumber}) - total markers: ${markers.current.length}`);
      }
    });

    console.log(`[MapInitializer] Created ${markers.current.length} markers total`);
    console.log(`[MapInitializer] Final marker to site mapping:`, markers.current.map((marker, idx) => ({
      markerIndex: idx,
      siteId: (marker as any).siteId,
      siteNumber: (marker as any).siteNumber
    })));
  }, [markers, onSiteSelect, googleMapRef]);

  // Component to use booking status hook for a single site and update its marker
  const SiteMarkerUpdater: React.FC<{ site: CampingSite; version: number }> = ({ site, version }) => {
    const dateRange = startDate && endDate ? { start: startDate, end: endDate } : undefined;
    const bookingStatus = useMapSiteBookingStatus(site.id, dateRange);

    console.log(`[SiteMarkerUpdater] Processing site ${site.id} (${site.siteNumber}) with booking status:`, bookingStatus.status);

    React.useEffect(() => {
      // Find the marker that matches this site ID (not by index!)
      const marker = markers.current.find(marker => (marker as any).siteId === site.id);
      
      if (!marker) {
        console.warn(`[SiteMarkerUpdater] No marker found for site ${site.id} (${site.siteNumber})`);
        console.warn(`[SiteMarkerUpdater] Available markers:`, markers.current.map(m => ({
          siteId: (m as any).siteId,
          siteNumber: (m as any).siteNumber
        })));
        return;
      }

      // Verify marker-site association
      const markerSiteId = (marker as any).siteId;
      const markerSiteNumber = (marker as any).siteNumber;
      
      console.log(`[SiteMarkerUpdater] Found marker for site ${site.id}:`);
      console.log(`  - Marker site ID: ${markerSiteId}, siteNumber: ${markerSiteNumber}`);
      console.log(`  - Expected site ID: ${site.id}, siteNumber: ${site.siteNumber}`);
      console.log(`  - Match: ${markerSiteId === site.id}`);
      
      if (markerSiteId !== site.id) {
        console.error(`[SiteMarkerUpdater] CRITICAL ERROR! Marker has siteId ${markerSiteId} but we're updating for site ${site.id}`);
        return;
      }

      console.log(`[SiteMarkerUpdater] Updating marker for site ${site.id} (${site.siteNumber}) with status:`, bookingStatus.status, 'color:', bookingStatus.color);

      // Update marker appearance based on booking status
      if (marker instanceof google.maps.marker.AdvancedMarkerElement && marker.content) {
        const container = marker.content as HTMLElement;
        const iconElement = container.querySelector('img');
        const labelElement = container.querySelector('div');

        if (iconElement && labelElement) {
          // Coming Soon overrides booking-status coloring
          if (site.status === 'coming_soon') {
            iconElement.style.filter = 'none';
            iconElement.style.opacity = '1';
            labelElement.textContent = 'Soon';
            labelElement.style.backgroundColor = 'hsl(24 95% 53%)';
            labelElement.style.border = '2px solid hsl(33 100% 96%)';
            marker.title = `${site.siteNumber || `Site ${site.id}`} (Coming Soon)`;
            console.log(`[SiteMarkerUpdater] Applied ORANGE (Coming Soon) styling to site ${site.id} (${site.siteNumber})`);
            return;
          }
          // Apply status-based styling
          if (bookingStatus.status === 'currently-booked') {
            iconElement.style.filter = 'hue-rotate(0deg) saturate(1.2)';
            iconElement.style.opacity = '0.9';
            labelElement.style.backgroundColor = bookingStatus.color; // This should be red (#ef4444)
            labelElement.style.border = `2px solid ${bookingStatus.backgroundColor}`;
            console.log(`[SiteMarkerUpdater] Applied RED styling to site ${site.id} (${site.siteNumber})`);
          } else if (bookingStatus.status === 'booked-for-dates') {
            iconElement.style.filter = 'hue-rotate(20deg) saturate(1.1)';
            iconElement.style.opacity = '0.8';
            labelElement.style.backgroundColor = bookingStatus.color; // This should be orange (#f97316)
            labelElement.style.border = `2px solid ${bookingStatus.backgroundColor}`;
            console.log(`[SiteMarkerUpdater] Applied ORANGE styling to site ${site.id} (${site.siteNumber})`);
          } else {
            iconElement.style.filter = 'none';
            iconElement.style.opacity = '1';
            labelElement.style.backgroundColor = bookingStatus.color; // This should be green (#22c55e)
            labelElement.style.border = `2px solid ${bookingStatus.backgroundColor}`;
            console.log(`[SiteMarkerUpdater] Applied GREEN styling to site ${site.id} (${site.siteNumber})`);
          }

          // Update title with correct site information
          const correctTitle = `${site.siteNumber || `Site ${site.id}`} - ${bookingStatus.label}`;
          marker.title = correctTitle;
          console.log(`[SiteMarkerUpdater] Updated marker title to: ${correctTitle}`);
        }
      } else {
        console.warn(`[SiteMarkerUpdater] Could not find container elements for site ${site.id} (${site.siteNumber})`);
      }
    }, [site, bookingStatus, version]);

    return null;
  };

  // Main map logic
  const initializeMap = React.useCallback(() => {
    if (mapInitialized || !mapRef.current || !mapsLoaded || sites.length === 0) return;
    if (typeof window === 'undefined' || !window.google?.maps) {
      setMapError("Google Maps API not available. Please check the console for more details.");
      return;
    }

    try {
      console.log(`[MapInitializer] Initializing map for site type: ${siteType}, location filter: ${locationFilter}`);
      console.log(`[MapInitializer] Sites to display:`, sites.map(s => ({ 
        id: s.id, 
        siteNumber: s.siteNumber,
        latitude: s.latitude,
        longitude: s.longitude 
      })));
      
      // Get center coordinates based on location filter and site type
      let center;
      let zoom = 13;
      if (locationFilter !== 'all') {
        // If a specific location is selected, use that
        center = LOCATION_COORDINATES[locationFilter] || getDefaultCenter().center;
        zoom = LOCATION_COORDINATES[locationFilter]?.zoom || 13;
      } else {
        // Default center based on site type
        const defaultCenter = getDefaultCenter();
        center = defaultCenter.center;
        zoom = defaultCenter.zoom;
      }

      const mapOptions: google.maps.MapOptions = { 
        center,
        zoom,
        mapId: 'd9ba7bb5885e8d91',
        gestureHandling: 'greedy',
        zoomControl: true,
        fullscreenControl: false,
        streetViewControl: false,
        mapTypeControl: true,
        mapTypeControlOptions: {
          style: google.maps.MapTypeControlStyle.DROPDOWN_MENU,
          position: google.maps.ControlPosition.TOP_LEFT,
        },
        rotateControl: false,
        tilt: 0,
      };
      const map = new google.maps.Map(mapRef.current, mapOptions);
      googleMapRef.current = map;

      clearMarkers();
      createMarkersForSites(map, sites);
      setMarkerVersion(v => v + 1);

      setMapInitialized(true);
      console.log(`[MapInitializer] Map initialized successfully with ${sites.length} sites`);
    } catch (error) {
      console.error(`[MapInitializer] Failed to initialize map:`, error);
      setMapError(`Failed to initialize map: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }, [
    mapInitialized,
    mapRef,
    sites,
    locationFilter,
    clearMarkers,
    googleMapRef,
    setMapError,
    setMapInitialized,
    mapsLoaded,
    siteType,
    getDefaultCenter,
    createMarkersForSites
  ]);

  // Update map when sites or location filter changes
  useEffect(() => {
    if (mapsLoaded) {
      if (!mapInitialized) {
        initializeMap();
      } else if (googleMapRef.current) {
        console.log(`[MapInitializer] Updating map for site type: ${siteType}, location filter: ${locationFilter}`);
        console.log(`[MapInitializer] Sites being updated:`, sites.map(s => ({ 
          id: s.id, 
          siteNumber: s.siteNumber,
          latitude: s.latitude,
          longitude: s.longitude 
        })));
        
        // Restore previous map view if available when updating markers
        const storedCenter = sessionStorage.getItem('mapCenter');
        const storedZoom = sessionStorage.getItem('mapZoom');
        
        const shouldRestoreView = storedCenter && storedZoom;
        
        // Update map center based on location filter or stored position
        if (shouldRestoreView) {
          const center = JSON.parse(storedCenter);
          const zoom = parseInt(storedZoom, 10);
          
          googleMapRef.current.setCenter(center);
          googleMapRef.current.setZoom(zoom);
        } else if (locationFilter !== 'all') {
          // Use location-specific coordinates
          const center = LOCATION_COORDINATES[locationFilter] || getDefaultCenter().center;
          const zoom = LOCATION_COORDINATES[locationFilter]?.zoom || 14;
          googleMapRef.current.setCenter(center);
          googleMapRef.current.setZoom(zoom);
        } else if (siteType === 'wild-horse') {
          // Default Wild Horse view
          const defaultCenter = getDefaultCenter();
          googleMapRef.current.setCenter(defaultCenter.center);
          googleMapRef.current.setZoom(defaultCenter.zoom);
        } else {
          // If showing all locations, fit bounds to include all markers
          if (sites.length > 0 && window.google?.maps) {
            try {
              const bounds = new window.google.maps.LatLngBounds();
              let hasValidCoordinates = false;
              
              sites.forEach(site => {
                if (site.latitude && site.longitude) {
                  bounds.extend({ lat: site.latitude, lng: site.longitude });
                  hasValidCoordinates = true;
                }
              });
              
              if (hasValidCoordinates) {
                googleMapRef.current.fitBounds(bounds);
              } else {
                // Fallback to default view if no valid coordinates
                const defaultCenter = getDefaultCenter();
                googleMapRef.current.setCenter(defaultCenter.center);
                googleMapRef.current.setZoom(defaultCenter.zoom);
              }
            } catch (err) {
              console.error('Error setting map bounds:', err);
              // Fallback
              const defaultCenter = getDefaultCenter();
              googleMapRef.current.setCenter(defaultCenter.center);
              googleMapRef.current.setZoom(defaultCenter.zoom);
            }
          }
        }

        // Refresh markers
        clearMarkers();
        createMarkersForSites(googleMapRef.current, sites);
        setMarkerVersion(v => v + 1);
      }
    }
  }, [
    sites,
    locationFilter,
    mapsLoaded,
    mapInitialized,
    initializeMap,
    clearMarkers,
    googleMapRef,
    siteType,
    getDefaultCenter,
    createMarkersForSites
  ]);

  // Enable source maps in development for easier debugging
  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      // This enables source maps in development
      const script = document.createElement('script');
      script.innerHTML = '//# sourceURL=MapInitializer.js';
      document.head.appendChild(script);
      return () => {
        document.head.removeChild(script);
      };
    }
  }, []);

  // Render SiteMarkerUpdater components for each site to handle booking status updates
  return (
    <>
      {mapInitialized && sites.map((site) => {
        console.log(`[MapInitializer] Rendering SiteMarkerUpdater for site ${site.id} (${site.siteNumber})`);
        return (
          <SiteMarkerUpdater 
            key={`${site.id}-updater`}
            site={site}
            version={markerVersion}
          />
        );
      })}
    </>
  );
};

export default MapInitializer;
